import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Spinner } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';

import { CreditCheckNav } from './components/CreditCheckNav';
import styles from './CreditCheck.module.css';
import { TOTAL } from './logic/questions.config';
import { IntroScreen } from './screens/IntroScreen';
import { QuestionScreen } from './screens/QuestionScreen';
import { ResultScreen } from './screens/ResultScreen';
import { useCreditCheck } from './useCreditCheck';
import { markCreditCheckVisited } from '../../helpers/creditCheckSession';
import { useAuth } from '../../hooks/useAuth';

const CreditCheck = () => {
  const { t } = useLingui();
  const { isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();
  const {
    phase,
    index,
    answers,
    currentQuestion,
    result,
    start,
    setAnswer,
    next,
    skip,
    back,
    restart,
  } = useCreditCheck({ trackingEnabled: !isLoading && !isLoggedIn });

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/kreditwunsch', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const pageLoadedRef = useRef(false);
  useEffect(() => {
    if (isLoading || isLoggedIn || pageLoadedRef.current) return;
    pageLoadedRef.current = true;
    markCreditCheckVisited();
    tracking.ampli.creditCheckPageLoaded();
  }, [isLoading, isLoggedIn]);

  if (isLoading || isLoggedIn) {
    return (
      <span role="status" aria-label={t`Loading`}>
        <Spinner />
      </span>
    );
  }

  return (
    <div className={styles.page}>
      <CreditCheckNav />
      <div className={styles.content}>
        <div className={styles.card}>
          {phase === 'intro' && <IntroScreen onStart={start} />}
          {phase === 'question' && currentQuestion && (
            <QuestionScreen
              key={currentQuestion.id}
              question={currentQuestion}
              index={index + 1}
              total={TOTAL}
              value={answers[currentQuestion.id]}
              onAnswer={(value) => setAnswer(currentQuestion.id, value)}
              onNext={next}
              onSkip={skip}
              onBack={back}
            />
          )}
          {phase === 'result' && (
            <ResultScreen
              result={result}
              answers={answers}
              onRestart={restart}
            />
          )}
        </div>
        <p className={styles.disclaimer}>
          <Trans>
            The iwoca credit check is a non-binding orientation and does not
            replace a lending decision. The final review is carried out
            individually after your complete application.
          </Trans>
        </p>
      </div>
    </div>
  );
};

export default CreditCheck;
